import { styled } from '@mui/material/styles';
import { Box, Paper, TextField, Typography, Button, FormControlLabel, Switch, Grid } from '@mui/material';

// Honey-inspired color palette (same as your general styles)
export const honeyOpeningColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main container
export const HoneyOpeningContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyOpeningColors.honeyBrown,
  maxWidth: '900px',
  margin: '0 auto',
  padding: theme.spacing(3, 0),
}));

// Header section
export const HoneyOpeningHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

// Title with honey gradient
export const HoneyOpeningTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyOpeningColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle
export const HoneyOpeningSubtitle = styled(Typography)(({ theme }) => ({
  color: honeyOpeningColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// Days container
export const HoneyDaysContainer = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${honeyOpeningColors.brightHoney}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  
  // Honey accent stripe
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${honeyOpeningColors.goldenHoney} 0%, ${honeyOpeningColors.brightHoney} 50%, ${honeyOpeningColors.honeycomb} 100%)`,
    borderRadius: '20px 20px 0 0',
  },
}));

// Day row
export const HoneyDayRow = styled(Grid)(({ theme, isOpen }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  marginBottom: theme.spacing(1),
  background: isOpen 
    ? `linear-gradient(135deg, ${honeyOpeningColors.brightHoney}05, ${honeyOpeningColors.goldenHoney}05)`
    : `${honeyOpeningColors.softCream}80`,
  border: `1px solid ${isOpen ? honeyOpeningColors.brightHoney : honeyOpeningColors.lightBrown}30`,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: isOpen
      ? `linear-gradient(135deg, ${honeyOpeningColors.brightHoney}10, ${honeyOpeningColors.goldenHoney}10)`
      : `${honeyOpeningColors.softCream}`,
    borderColor: isOpen ? honeyOpeningColors.goldenHoney : honeyOpeningColors.lightBrown,
  },
}));

// Day label
export const HoneyDayLabel = styled(Typography)(({ theme, isOpen }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: isOpen ? honeyOpeningColors.honeyBrown : honeyOpeningColors.lightBrown,
  display: 'flex',
  alignItems: 'center',
  minWidth: '100px',
}));

// Status switch
export const HoneyStatusSwitch = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: honeyOpeningColors.honeyBrown,
  },
  
  '& .MuiSwitch-root': {
    '& .MuiSwitch-switchBase': {
      '&.Mui-checked': {
        color: honeyOpeningColors.goldenHoney,
        
        '& + .MuiSwitch-track': {
          backgroundColor: honeyOpeningColors.brightHoney,
        },
      },
    },
    
    '& .MuiSwitch-track': {
      backgroundColor: honeyOpeningColors.lightBrown,
    },
  },
}));

// Time input
export const HoneyTimeField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: 'white',
    fontSize: '0.9rem',
    
    '& fieldset': {
      borderColor: honeyOpeningColors.brightHoney,
      borderWidth: '1.5px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyOpeningColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyOpeningColors.goldenHoney,
      boxShadow: `0 0 0 2px ${honeyOpeningColors.goldenHoney}20`,
    },
  },
  
  '& .MuiInputBase-input': {
    fontSize: '0.9rem',
    color: honeyOpeningColors.honeyBrown,
    padding: theme.spacing(1),
  },
  
  '& .MuiInputLabel-root': {
    color: honeyOpeningColors.honeyBrown,
    fontSize: '0.85rem',
    
    '&.Mui-focused': {
      color: honeyOpeningColors.goldenHoney,
    },
  },
}));

// Time separator
export const HoneyTimeSeparator = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: honeyOpeningColors.honeycomb,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
}));

// Status badge
export const HoneyStatusBadge = styled(Box)(({ theme, isOpen }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '20px',
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  background: isOpen 
    ? `linear-gradient(135deg, ${honeyOpeningColors.goldenHoney}20, ${honeyOpeningColors.brightHoney}20)`
    : `${honeyOpeningColors.lightBrown}20`,
  color: isOpen ? honeyOpeningColors.honeyBrown : honeyOpeningColors.lightBrown,
  border: `1px solid ${isOpen ? honeyOpeningColors.goldenHoney : honeyOpeningColors.lightBrown}`,
  
  '&::before': {
    content: '""',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: theme.spacing(0.5),
    background: isOpen ? honeyOpeningColors.goldenHoney : honeyOpeningColors.lightBrown,
  },
}));

// Save button
export const HoneySaveButton = styled(Button)(({ theme }) => ({
  background: honeyOpeningColors.honeyGradient,
  color: honeyOpeningColors.honeyBrown,
  borderRadius: '50px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 700,
  fontSize: '1.1rem',
  boxShadow: `0 8px 25px ${honeyOpeningColors.goldenHoney}40`,
  transition: 'all 0.3s ease',
  textTransform: 'none',
  
  '&:hover': {
    background: honeyOpeningColors.honeycombGradient,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 35px ${honeyOpeningColors.goldenHoney}60`,
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
    background: honeyOpeningColors.honeyGradient,
  },
}));

// Actions container
export const HoneyActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
}));

// Time inputs container
export const HoneyTimeInputsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(2),
  
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    marginTop: theme.spacing(1),
    justifyContent: 'flex-start',
  },
}));

// Responsive grid item
export const HoneyGridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));